import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input-simple';
import { Button } from '../../components/ui/button-simple';
import { Search, MapPin, Users, Eye, AlertCircle, ChevronLeft, ChevronRight, X, Building2, Sprout, Award, Plus, DollarSign, BookOpen, ShieldCheck, MessageSquare } from 'lucide-react';
import { municipalities } from '../../data/municipalities';
import { farmersAPI, profileRequestAPI } from '../../services/api';
import { useFarmers } from '../../hooks/useApi';
import { RequestPostingModal } from '../../components/forms/RequestPostingModal';

// Add animations
const styles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in-up { animation: fadeInUp 0.5s ease-out forwards; }
  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.4s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.5s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.6s; }

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .animate-slide-down { animation: slideDown 0.6s ease-out forwards; }

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  .animate-slide-up { animation: slideUp 0.4s ease-out forwards; }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-farmer-account-animations]')) {
    styleSheet.setAttribute('data-farmer-account-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export function FarmersAccountDirectory({ user }) {
  const { farmers, loading, error, fetchFarmers } = useFarmers();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('all');
  const [selectedOwnership, setSelectedOwnership] = useState('all');
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [showPostingModal, setShowPostingModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    const loadData = async () => {
      try { await fetchFarmers(() => farmersAPI.getAll()); } catch {}
    };
    loadData();
  }, [fetchFarmers]);

  const filteredFarmers = useMemo(() => {
    return farmers.filter(farmer => {
      if (farmer.id === null || farmer.id === undefined) return false;
      const matchesSearch =
        (farmer.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (farmer.municipality || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (farmer.address || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMunicipality = selectedMunicipality === 'all' || farmer.municipality === selectedMunicipality;
      const matchesOwnership = selectedOwnership === 'all' ||
        (farmer.landOwnership || '').toLowerCase().includes(selectedOwnership.toLowerCase());
      return matchesSearch && matchesMunicipality && matchesOwnership;
    });
  }, [farmers, searchTerm, selectedMunicipality, selectedOwnership]);

  const paginatedFarmers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredFarmers.slice(startIndex, endIndex);
  }, [filteredFarmers, currentPage, itemsPerPage]);

  const navigate = useNavigate();
  const totalPages = Math.ceil(filteredFarmers.length / itemsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedMunicipality, selectedOwnership]);

  const [postingRequest, setPostingRequest] = useState(null);

  useEffect(() => {
    if (user?.id) {
      profileRequestAPI.getMyRequest(user.id)
        .then(res => { if (res.success) setPostingRequest(res.request); })
        .catch(() => {});
    }
  }, [user]);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="mb-8 bg-gradient-to-r from-emerald-700 via-green-700 to-teal-800 rounded-2xl shadow-xl p-6 text-white animate-slide-down relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur-md p-3.5 rounded-2xl shadow-inner">
                  <Sprout className="h-8 w-8 text-emerald-200" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-extrabold tracking-tight">Cacao Farmers Directory</h1>
                    <span className="bg-emerald-500/40 text-emerald-100 border border-emerald-300/40 text-xs px-2.5 py-0.5 rounded-full font-medium">
                      Member Verified Access
                    </span>
                  </div>
                  <p className="text-emerald-100 max-w-2xl text-sm mt-1">
                    Full directory access for registered Industry Accounts — view complete profile data, farm metrics, and contact info.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Action Banner for Farmers */}
          {user?.role === 'farmer' && (
            postingRequest ? (
              <div className="mb-6 bg-gradient-to-r from-emerald-700 to-teal-800 rounded-2xl shadow-lg p-4 text-white flex items-center justify-between gap-4 border border-emerald-400/30">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6 text-emerald-200 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Public Listing Status: <span className="uppercase font-extrabold text-amber-200">{postingRequest.status}</span></p>
                    <p className="text-xs text-emerald-100">You can only request public listing once. You can edit your profile details in your Profile tab.</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/profile')}
                  className="flex-shrink-0 flex items-center gap-2 bg-white text-emerald-800 font-bold text-xs px-4 py-2.5 rounded-xl hover:bg-emerald-50 shadow transition"
                >
                  Manage in Profile
                </button>
              </div>
            ) : (
              <div className="mb-6 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl shadow-lg p-4 text-white flex items-center justify-between gap-4 border border-green-400/30">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-6 h-6 text-green-200 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Are you a Cacao Farmer?</p>
                    <p className="text-xs text-green-100">Request to publish your farm profile in this verified directory (One-time request).</p>
                  </div>
                </div>
                <button
                  id="btn-farmer-account-quick-request"
                  onClick={() => setShowPostingModal(true)}
                  className="flex-shrink-0 flex items-center gap-2 bg-white text-green-800 font-bold text-xs px-4 py-2.5 rounded-xl hover:bg-green-50 shadow transition"
                >
                  <Plus className="w-4 h-4" />
                  Request Public Listing
                </button>
              </div>
            )
          )}

          {/* Search and Filters */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-emerald-100">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-500 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search by farmer name, sitio or municipality..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl"
                  aria-label="Search farmers"
                />
              </div>

              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-500 h-5 w-5 z-10" />
                <select
                  value={selectedMunicipality}
                  onChange={(e) => setSelectedMunicipality(e.target.value)}
                  className="pl-10 w-full h-12 border border-emerald-200 rounded-xl focus:border-emerald-500 focus:ring-emerald-500 bg-white appearance-none cursor-pointer text-sm"
                  aria-label="Filter by municipality"
                >
                  <option value="all">All Municipalities</option>
                  {municipalities.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-500 h-5 w-5 z-10" />
                <select
                  value={selectedOwnership}
                  onChange={(e) => setSelectedOwnership(e.target.value)}
                  className="pl-10 w-full h-12 border border-emerald-200 rounded-xl focus:border-emerald-500 focus:ring-emerald-500 bg-white appearance-none cursor-pointer text-sm"
                  aria-label="Filter by ownership type"
                >
                  <option value="all">All Ownership Types</option>
                  <option value="owned">Owned</option>
                  <option value="rented">Rented</option>
                  <option value="tenanted">Tenanted</option>
                </select>
              </div>
            </div>

            <div className="mt-4 text-sm flex items-center justify-between">
              <span className="text-gray-600">
                Showing <span className="font-bold text-emerald-700">{filteredFarmers.length}</span> registered cacao farmers
              </span>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center items-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                <p className="text-emerald-800 font-medium">Loading farmer directory...</p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-red-800 font-medium">Error loading farmers</span>
              </div>
              <p className="text-red-600 mt-2">{error}</p>
              <Button onClick={() => window.location.reload()} className="mt-4 bg-red-600 hover:bg-red-700">
                Retry
              </Button>
            </div>
          )}

          {/* Farmers Grid */}
          {!loading && !error && filteredFarmers.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedFarmers.map((farmer) => (
                  <div
                    key={farmer.id}
                    className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-emerald-100 hover:border-emerald-300 overflow-hidden flex flex-col animate-fade-in-up"
                    style={{ height: '400px' }}
                  >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-3.5 border-b border-emerald-100 flex-shrink-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base text-emerald-950 font-bold group-hover:text-emerald-700 transition-colors truncate">
                            {farmer.name || 'Unnamed Farmer'}
                          </h3>
                          <div className="flex items-center mt-1 text-gray-600 text-xs">
                            <MapPin className="h-3.5 w-3.5 mr-1 text-emerald-600 flex-shrink-0" />
                            <span className="truncate">{farmer.address || farmer.municipality || 'Bohol'}</span>
                          </div>
                        </div>
                        <span className="bg-emerald-100 text-emerald-800 text-[11px] font-semibold px-2 py-0.5 rounded-full ml-2 flex-shrink-0">
                          {farmer.landOwnership || 'Owned'}
                        </span>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-3 flex-1 flex flex-col overflow-y-auto">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center justify-between p-2 bg-emerald-50/50 rounded-xl text-xs">
                          <div className="flex items-center gap-2">
                            <Sprout className="h-4 w-4 text-emerald-600" />
                            <span className="text-gray-600 font-medium">Farm Area</span>
                          </div>
                          <span className="font-bold text-gray-900">{farmer.farmSize || 'N/A'} ha</span>
                        </div>

                        <div className="flex items-center justify-between p-2 bg-emerald-50/50 rounded-xl text-xs">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-blue-600" />
                            <span className="text-gray-600 font-medium">Trees Planted</span>
                          </div>
                          <span className="font-bold text-gray-900">{farmer.actualNumberOfTreesPlanted || 'N/A'}</span>
                        </div>

                        <div className="flex items-start justify-between p-2 bg-emerald-50/50 rounded-xl text-xs">
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Award className="h-4 w-4 text-amber-600" />
                            <span className="text-gray-600 font-medium">Varieties</span>
                          </div>
                          <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                            {(farmer.cacaoVarieties || []).slice(0, 3).map((v, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800">
                                {v}
                              </span>
                            ))}
                          </div>
                        </div>

                        {farmer.annualFarmIncome && (
                          <div className="flex items-center justify-between p-2 bg-emerald-50/50 rounded-xl text-xs">
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-green-600" />
                              <span className="text-gray-600 font-medium">Annual Income</span>
                            </div>
                            <span className="font-bold text-green-700">₱{Number(farmer.annualFarmIncome).toLocaleString()}</span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-3 flex gap-2 flex-shrink-0">
                        <Button
                          className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white font-semibold text-xs flex items-center justify-center gap-1.5 rounded-xl shadow transition-all py-2"
                          onClick={() => setSelectedFarmer(farmer)}
                        >
                          <Eye className="h-4 w-4" />
                          View Profile
                        </Button>
                        <Button
                          className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs flex items-center justify-center gap-1.5 rounded-xl shadow transition-all px-3.5 py-2"
                          onClick={() => navigate(`/messages?partnerId=farmer_${farmer.id}&partnerName=${encodeURIComponent(farmer.name)}&partnerRole=farmer`)}
                          title="Message Farmer"
                        >
                          <MessageSquare className="h-4 w-4" />
                          Message
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-8 bg-white p-6 rounded-2xl shadow-lg border border-emerald-100">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="flex items-center gap-2 px-6 rounded-xl hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Page</span>
                    <span className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-700 text-white font-bold rounded-xl shadow-md">
                      {currentPage}
                    </span>
                    <span className="text-sm text-gray-600">of {totalPages}</span>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="flex items-center gap-2 px-6 rounded-xl hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {filteredFarmers.length === 0 && !loading && !error && (
            <div className="text-center py-16 bg-white rounded-2xl border border-emerald-100 shadow-sm">
              <Users className="h-14 w-14 text-emerald-300 mx-auto mb-3" />
              <h3 className="text-gray-700 font-semibold text-base mb-1">No farmers found</h3>
              <p className="text-gray-500 text-sm">Try adjusting your search or municipality filter.</p>
            </div>
          )}

          {/* Farmer Full Info Detail Modal */}
          {selectedFarmer && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 animate-fade-in backdrop-blur-sm">
              <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-slide-up flex flex-col">
                <div className="bg-gradient-to-r from-emerald-700 to-teal-800 p-5 text-white flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2.5 rounded-xl">
                      <Sprout className="h-6 w-6 text-emerald-200" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">{selectedFarmer.name}</h2>
                      <p className="text-emerald-100 text-xs">{selectedFarmer.address || selectedFarmer.municipality || 'Farmer Profile'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedFarmer(null)}
                    className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-xl transition"
                    aria-label="Close profile"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4 p-6 overflow-y-auto flex-1 text-sm text-gray-700">
                  {/* Basic Personal Info */}
                  <div className="bg-emerald-50/60 p-4 rounded-xl border border-emerald-100">
                    <h3 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-600" /> Personal & Household Details
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div><span className="text-gray-500">Full Name:</span> <strong className="text-gray-900">{selectedFarmer.name}</strong></div>
                      <div><span className="text-gray-500">Age:</span> <strong className="text-gray-900">{selectedFarmer.age ? `${selectedFarmer.age} yrs` : 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Gender:</span> <strong className="text-gray-900">{selectedFarmer.gender || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Civil Status:</span> <strong className="text-gray-900">{selectedFarmer.civilStatus || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Education:</span> <strong className="text-gray-900">{selectedFarmer.educationalAttainment || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Household Members:</span> <strong className="text-gray-900">{selectedFarmer.noOfHouseholdMembers || 'N/A'}</strong></div>
                    </div>
                  </div>

                  {/* Farm Details */}
                  <div className="bg-emerald-50/60 p-4 rounded-xl border border-emerald-100">
                    <h3 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
                      <Sprout className="w-4 h-4 text-emerald-600" /> Farm Specifications
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div><span className="text-gray-500">Farm Area:</span> <strong className="text-gray-900">{selectedFarmer.farmSize} hectares</strong></div>
                      <div><span className="text-gray-500">Land Ownership:</span> <strong className="text-gray-900">{selectedFarmer.landOwnership || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Trees Planted:</span> <strong className="text-gray-900">{selectedFarmer.actualNumberOfTreesPlanted || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Year Established:</span> <strong className="text-gray-900">{selectedFarmer.yearEstablished || 'N/A'}</strong></div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Cacao Varieties:</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {(selectedFarmer.cacaoVarieties || []).map((v, i) => (
                            <span key={i} className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 font-semibold rounded-full text-[11px]">
                              {v}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Income & Affiliation */}
                  <div className="bg-emerald-50/60 p-4 rounded-xl border border-emerald-100">
                    <h3 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-emerald-600" /> Income & Cooperative Affiliation
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div><span className="text-gray-500">Annual Farm Income:</span> <strong className="text-green-700">{selectedFarmer.annualFarmIncome ? `₱${Number(selectedFarmer.annualFarmIncome).toLocaleString()}` : 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Cooperative/Affiliation:</span> <strong className="text-gray-900">{selectedFarmer.affiliationName || 'None / Independent'}</strong></div>
                      <div><span className="text-gray-500">Position:</span> <strong className="text-gray-900">{selectedFarmer.position || 'N/A'}</strong></div>
                      <div><span className="text-gray-500">Years in Membership:</span> <strong className="text-gray-900">{selectedFarmer.yearsInMembership || 'N/A'}</strong></div>
                    </div>
                  </div>

                  {/* Productivity & Knowledge */}
                  {(selectedFarmer.podsPerTreeHarvest || selectedFarmer.beansHarvestKg || selectedFarmer.knowledgeCacao) && (
                    <div className="bg-emerald-50/60 p-4 rounded-xl border border-emerald-100">
                      <h3 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-emerald-600" /> Productivity & Farming Knowledge
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {selectedFarmer.podsPerTreeHarvest && <div><span className="text-gray-500">Pods/Tree Harvest:</span> <strong className="text-gray-900">{selectedFarmer.podsPerTreeHarvest}</strong></div>}
                        {selectedFarmer.beansHarvestKg && <div><span className="text-gray-500">Beans/ha (kg):</span> <strong className="text-gray-900">{selectedFarmer.beansHarvestKg} kg</strong></div>}
                        {selectedFarmer.knowledgeCacao && <div className="col-span-2"><span className="text-gray-500">Knowledge Level:</span> <strong className="text-gray-900">{selectedFarmer.knowledgeCacao}</strong></div>}
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-4 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0 bg-gray-50">
                  <Button
                    onClick={() => {
                      const f = selectedFarmer;
                      setSelectedFarmer(null);
                      navigate(`/messages?partnerId=farmer_${f.id}&partnerName=${encodeURIComponent(f.name)}&partnerRole=farmer`);
                    }}
                    className="px-5 bg-amber-600 hover:bg-amber-700 text-white font-semibold flex items-center gap-2 rounded-xl"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Message Farmer
                  </Button>
                  <Button
                    onClick={() => setSelectedFarmer(null)}
                    className="px-6 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Posting Request Modal for Farmer role */}
      {showPostingModal && user && (
        <RequestPostingModal
          user={user}
          onClose={() => setShowPostingModal(false)}
          onSuccess={() => setShowPostingModal(false)}
        />
      )}
    </>
  );
}
